import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Loader2, Search, Star, X } from "lucide-react";
import { api, apiError, fmtDate } from "@/lib/api";

const STATUS_CLS = {
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  rejected: "border-red-500/40 bg-red-500/10 text-red-400",
};

const StarsRow = ({ value }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= value ? "fill-amber-500 text-amber-500" : "fill-neutral-800 text-neutral-800"} />
    ))}
  </span>
);

export default function AdminReviews() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [rating, setRating] = useState(0);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), page_size: "20" });
    if (status) params.set("status", status);
    if (q.trim()) params.set("q", q.trim());
    if (rating) params.set("rating", String(rating));
    api.get(`/admin/reviews?${params}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load reviews")));
  }, [status, q, rating, page]);
  useEffect(load, [load]);

  const moderate = async (r, action) => {
    setBusyId(r.id + action);
    try {
      await api.post(`/admin/reviews/${r.id}/${action}`, { note: "" });
      toast.success(`Review ${action}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Moderation failed"));
    } finally {
      setBusyId(null);
    }
  };

  const counts = data?.counts || {};
  const chips = [
    { id: "pending", label: `Pending (${counts.pending ?? 0})` },
    { id: "approved", label: `Approved (${counts.approved ?? 0})` },
    { id: "rejected", label: `Rejected (${counts.rejected ?? 0})` },
    { id: "", label: "All" },
  ];

  const actions = (r) => (
    <span className="flex items-center gap-2">
      {r.status !== "approved" && (
        <button onClick={(e) => { e.stopPropagation(); moderate(r, "approve"); }}
          disabled={busyId} data-testid={`review-approve-${r.id}`}
          className="flex h-8 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 text-[11px] font-bold text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-40">
          {busyId === r.id + "approve" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Approve
        </button>
      )}
      {r.status !== "rejected" && (
        <button onClick={(e) => { e.stopPropagation(); moderate(r, "reject"); }}
          disabled={busyId} data-testid={`review-reject-${r.id}`}
          className="flex h-8 items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-3 text-[11px] font-bold text-red-300 hover:bg-red-500/20 disabled:opacity-40">
          {busyId === r.id + "reject" ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />} Reject
        </button>
      )}
    </span>
  );

  const expanded = (r) => (
    <div className="space-y-2 border-t border-white/[0.07] pt-3" data-testid={`review-detail-${r.id}`}>
      <p className="text-sm font-bold text-white">{r.title}</p>
      <p className="text-sm text-neutral-400">{r.text}</p>
      <p className="text-[11px] text-neutral-600">
        {r.user_name} · {r.verified ? "verified purchase" : "unverified"} · submitted {fmtDate(r.created_at)}
        {r.edited_at ? ` · edited ${fmtDate(r.edited_at)}` : ""}
      </p>
      {(r.history || []).length > 0 && (
        <div className="pt-1" data-testid={`review-audit-${r.id}`}>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-neutral-600">Audit history</p>
          {[...r.history].reverse().map((h, i) => (
            <p key={i} className="text-[11px] text-neutral-500">
              {String(h.at).slice(0, 16).replace("T", " ")} · <strong className="text-neutral-300">{h.action}</strong> · {h.actor}
              {h.details ? ` — ${h.details}` : ""}
            </p>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="admin-reviews" className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
          <Star className="h-6 w-6 text-amber-400" /> Reviews
        </h1>
        <p className="mt-1 text-sm text-neutral-500">Moderate customer reviews — approved ones appear on product pages.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {chips.map((c) => (
          <button key={c.id} onClick={() => { setStatus(c.id); setPage(1); }} data-testid={`reviews-filter-${c.id || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${status === c.id ? "bg-amber-500 text-black" : "border border-white/10 text-neutral-400 hover:text-white"}`}>
            {c.label}
          </button>
        ))}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search product, customer, text…"
            data-testid="reviews-search"
            className="h-10 w-64 max-w-full rounded-full border border-white/10 bg-black/40 pl-9 pr-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
        <select value={rating} onChange={(e) => { setRating(parseInt(e.target.value)); setPage(1); }} data-testid="reviews-rating"
          className="h-10 rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white focus:border-amber-500/60 focus:outline-none">
          <option value={0}>Any rating</option>
          {[5, 4, 3, 2, 1].map((s) => <option key={s} value={s}>{s} star{s > 1 ? "s" : ""}</option>)}
        </select>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : data.reviews.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500" data-testid="reviews-empty">
          No reviews here.
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-2xl border border-white/[0.07] md:block">
            <table className="w-full text-left text-sm" data-testid="reviews-table">
              <thead>
                <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                  <th className="w-8 px-3 py-3"></th><th className="px-3 py-3">Product</th><th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Rating</th><th className="px-3 py-3">Review</th><th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3">Status</th><th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.reviews.map((r) => (
                  <Fragment key={r.id}>
                    <tr onClick={() => setOpenId(openId === r.id ? null : r.id)}
                      className="cursor-pointer border-b border-white/[0.05] hover:bg-white/[0.02]" data-testid={`review-row-${r.id}`}>
                      <td className="px-3 py-3 text-neutral-500">
                        <ChevronDown className={`h-4 w-4 transition-transform ${openId === r.id ? "rotate-180" : ""}`} />
                      </td>
                      <td className="max-w-[180px] truncate px-3 py-3 text-neutral-300">{r.product_name}</td>
                      <td className="px-3 py-3 text-neutral-400">{r.user_name}</td>
                      <td className="px-3 py-3"><StarsRow value={r.rating} /></td>
                      <td className="max-w-[240px] truncate px-3 py-3 text-xs text-neutral-400">{r.title} — {r.text}</td>
                      <td className="px-3 py-3 text-xs text-neutral-500">{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[r.status] || ""}`}
                          data-testid={`review-status-${r.id}`}>{r.status}</span>
                      </td>
                      <td className="px-3 py-3"><span className="flex justify-end">{actions(r)}</span></td>
                    </tr>
                    {openId === r.id && (
                      <tr className="border-b border-white/[0.05] bg-white/[0.015]">
                        <td colSpan={8} className="px-6 py-4">{expanded(r)}</td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden" data-testid="reviews-cards">
            {data.reviews.map((r) => (
              <div key={r.id} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid={`review-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{r.product_name}</p>
                    <p className="text-xs text-neutral-500">{r.user_name} · {fmtDate(r.created_at)}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
                </div>
                <div className="mt-2"><StarsRow value={r.rating} /></div>
                <p className="mt-1.5 text-xs text-neutral-400"><strong className="text-neutral-200">{r.title}</strong> — {r.text}</p>
                <button onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`review-expand-${r.id}`}
                  className="mt-2 text-[11px] font-bold text-amber-400">
                  {openId === r.id ? "Hide audit" : "Audit history"}
                </button>
                {openId === r.id && <div className="mt-2">{expanded(r)}</div>}
                <div className="mt-3">{actions(r)}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between" data-testid="reviews-pagination">
            <p className="text-xs text-neutral-500">{data.total} review(s) · page {data.page} of {data.pages}</p>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="reviews-prev"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="reviews-next"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
